import type { ShareableField } from '../types';
import { getPublicAppOrigin } from './publicAppUrl';

export const PUBLIC_PASSPORT_FIELD_OPTIONS: {
  field: ShareableField;
  label: string;
  description: string;
}[] = [
  { field: 'photo', label: 'Profile Photo', description: 'Your avatar/profile picture' },
  { field: 'full_name', label: 'Full Name', description: 'First and last name' },
  { field: 'bio', label: 'Bio / Summary', description: 'Your professional summary' },
  { field: 'email', label: 'Email Address', description: 'Your contact email' },
  { field: 'phone', label: 'Phone Number', description: 'Your contact phone number' },
  { field: 'work_experience', label: 'Work Experience', description: 'Your full employment history' },
  { field: 'qualifications', label: 'Qualifications', description: 'Certifications, degrees and licenses' },
  { field: 'references', label: 'References', description: 'Your professional references' },
  { field: 'official_documents', label: 'Official Documents', description: 'ID document and police clearance uploads' },
];

export const DEFAULT_PUBLIC_PASSPORT_FIELDS: ShareableField[] = [
  'photo',
  'full_name',
  'bio',
  'work_experience',
  'qualifications',
];

export function buildPublicPassportUrl(token: string): string {
  return `${getPublicAppOrigin()}/p/${token}`;
}

export function employeeQrFileSlug(
  firstName?: string | null,
  lastName?: string | null,
  token?: string,
): string {
  const name = [firstName, lastName]
    .filter(Boolean)
    .join('-')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  const suffix = token ? token.slice(0, 8) : 'qr';
  return `${name || 'passport'}-${suffix}`;
}

export async function generatePassportQrPng(url: string, width = 1024): Promise<string> {
  const QRCode = (await import('qrcode')).default;
  return QRCode.toDataURL(url, {
    width,
    margin: 2,
    errorCorrectionLevel: 'M',
  });
}

export async function generatePassportQrSvg(url: string): Promise<string> {
  const QRCode = (await import('qrcode')).default;
  return QRCode.toString(url, {
    type: 'svg',
    width: 1024,
    margin: 2,
    errorCorrectionLevel: 'M',
  });
}

export function downloadDataUrl(dataUrl: string, filename: string): void {
  const link = document.createElement('a');
  link.href = dataUrl;
  link.download = filename;
  link.click();
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export function csvEscape(value: string): string {
  if (/[",\n\r]/.test(value)) return `"${value.replace(/"/g, '""')}"`;
  return value;
}

export function buildPublicPassportCsv(
  rows: Array<{
    name: string;
    email: string;
    url: string;
    visible: boolean;
  }>,
): string {
  const header = 'name,email,url,visible';
  const lines = rows.map(row =>
    [
      csvEscape(row.name),
      csvEscape(row.email),
      csvEscape(row.url),
      row.visible ? 'yes' : 'no',
    ].join(','),
  );
  return [header, ...lines].join('\n');
}
